import { useMutationHook, useQueryHook } from "@/hooks"
import {
  createSession,
  createSessionCrop,
  createSessionData,
  deleteSession,
  deleteSessionCrop,
  deleteSessionData,
  readSession,
  readSessionDataItems,
  readSessionTypeStats,
  updateSessionRotation,
  upsertSession,
  upsertSessionTime,
} from "@/app/api/session"
import { useQueries, useQueryClient } from "@tanstack/react-query"

export const useSessionQuery = (idfSession: number) => {
  return useQueryHook({
    queryKey: ["readSession", idfSession],
    queryFn: () => readSession(idfSession),
  })
}

export const useSessionTypeStatsQuery = () => {
  return useQueryHook({
    queryKey: ["readSessionTypeStats"],
    queryFn: () => readSessionTypeStats(),
  })
}

export const useSessionDataItemListQuery = (idfSessionList: number[]) => {
  return useQueries({
    queries: idfSessionList.map((idfSession) => ({
      queryKey: ["readSessionDataItems", idfSession],
      queryFn: () => readSessionDataItems(idfSession),
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data?.response),
        pending: results.some((result) => result.isPending),
      }
    },
  })
}

export const useSessionDataItemsQuery = (idfSession: number) => {
  return useQueryHook({
    queryKey: ["readSessionDataItems", idfSession],
    queryFn: () => readSessionDataItems(idfSession),
  })
}

export const useCreateSessionMutation = () => {
  return useMutationHook(createSession, [
    ["readSession"],
    ["readSessionList"],
    ["readAnalyzingActivity"],
  ])
}

export const useUpsertSessionMutation = () => {
  const queryClient = useQueryClient()

  // readSessionDataItems는 수정된 세션만 targeted invalidate
  // (broad invalidate 시 세션 N개의 쿼리가 동시 refetch되어 page.tsx의
  //  스토어 동기화 useEffect가 setState 연쇄를 일으킴 — Maximum update depth 크래시)
  return useMutationHook(upsertSession, undefined, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["readSession"] })
      queryClient.invalidateQueries({ queryKey: ["readSessionList"] })
      queryClient.invalidateQueries({
        queryKey: ["readSessionDataItems", variables.idfSession],
      })
      queryClient.invalidateQueries({ queryKey: ["readAnalyzingActivity"] })
    },
  })
}

export const useDeleteSessionMutation = (idfActivity?: number) => {
  return useMutationHook(deleteSession, [
    ["readSession"],
    ["readSessionList", idfActivity],
    ["readAnalyzingActivity"],
  ])
}

export const useCreateSessionDataMutation = () => {
  const queryClient = useQueryClient()

  // readSessionDataItems는 해당 세션만 targeted invalidate (broad 시 setState 연쇄 크래시 — 위 주석 참고)
  return useMutationHook(createSessionData, undefined, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["readSessionDataItems", variables.params.idfSession],
      })
      queryClient.invalidateQueries({ queryKey: ["readSessionList"] })
      queryClient.invalidateQueries({ queryKey: ["readAnalyzingActivity"] })
    },
  })
}

export const useDeleteSessionDataMutation = () => {
  const queryClient = useQueryClient()

  // readSessionDataItems는 해당 세션만 targeted invalidate (broad 시 setState 연쇄 크래시 — 위 주석 참고)
  return useMutationHook(deleteSessionData, undefined, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["readSessionDataItems", variables.params.idfSession],
      })
      queryClient.invalidateQueries({ queryKey: ["readSessionList"] })
      queryClient.invalidateQueries({ queryKey: ["readAnalyzingActivity"] })
    },
  })
}

export const useUpsertSessionTimeMutation = () => {
  return useMutationHook(upsertSessionTime)
}

export const useCreateSessionCropMutation = () => {
  return useMutationHook(createSessionCrop, [
    ["readAnalyzingActivity"],
    ["readActivitiesAnalysisStatus"],
  ])
}

export const useDeleteSessionCropMutation = () => {
  return useMutationHook(deleteSessionCrop, [
    ["readAnalyzingActivity"],
    ["readActivitiesAnalysisStatus"],
  ])
}

export const useUpdateSessionRotationMutation = () => {
  return useMutationHook(updateSessionRotation, [["readActivityDetail"], ["readAnalyzingActivity"]])
}
